import * as React from 'react'
import { useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    tokens,
} from '@fluentui/react-components'
import { CalendarDayRegular, CalendarMonthRegular, CalendarWorkWeekRegular } from '@fluentui/react-icons'
import useModules from '../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import UserPicker from '../../components/TagPicker/UserPicker'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType, generateFromDate, generateToDate } from '../BaseOverview/BaseOverviewFilter'
import DateRange from '../../components/DatePicker/DateRange'
import BooleanCombobox from '../../components/Combobox/BooleanCombobox'
import FilePicker from '../../components/TagPicker/FilePicker'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import { DateTime } from 'luxon'
import { TravelOverviewDto } from '../../config/registrationConfig'
import { MoneyDto } from '../../config/i18nConfig'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import TravelListItem from './TravelListItem'
import TravelHeaderItem from './TravelHeaderItem'
import { multiplyRate } from '../../helpers/numberHelper'

export const useItemStyles = makeStyles({
    listTravelItem: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) var(--baseOverviewTimeWidth) auto var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
    },
    listTravelItemSelectable: {
        gridTemplateColumns:
            'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) var(--baseOverviewTimeWidth) auto min-content var(--baseOverviewPriceWidth) min-content',
    },
    listTravelItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto 1fr',
    },
    listTravelItemSmallSelectable: {
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto 1fr',
    },
    listTravelItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewTimeWidth) var(--baseOverviewPriceWidth) min-content',
        gap: tokens.spacingVerticalS,
    },
    listTravelItemOverflow: {
        overflowWrap: 'anywhere',
    },
    turnoverField: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingVerticalS,
    },
})

type BaseItem = TravelOverviewDto

export type OverviewItem = {
    // Is calculated during runtime and not returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
} & BaseItem

export const getTravelTurnover = (rate: MoneyDto, item: OverviewItem): MoneyDto => {
    if (!rate) {
        return undefined
    }

    if (!item) {
        return {
            amount: '0.00',
            currency: rate.currency,
        }
    }

    // An item only has the turnover property after the filters have applied during the mounting of this TravelList,
    // therefore calculate it when no turnover has been calculated.
    return multiplyRate(item.distance, rate)
}

export type TravelOverviewProps = BaseOverviewProps<ExtendedTravelOverviewDto, TravelFilterType, TravelSortingType, TravelOverviewCount>

export type BaseTravelOverviewProps = Partial<TravelOverviewProps> & {
    baseUrl: string

    fileId?: string
    hideFileField?: boolean
    hideInvoiceField?: boolean
    renderLink?: boolean
}

export type BaseTravelLineOverviewProps = BaseTravelOverviewProps &
    BaseOverviewLineItemProps<ExtendedTravelOverviewDto, TravelFilterType, TravelSortingType>

export type TravelOverviewCount = BaseOverviewCount & {
    distance: number
    currencies: TravelCurrencyOverviewCount[]
}

export type TravelCurrencyOverviewCount = {
    currency: string
    distance: number
    turnover: string
    invoiceTurnover: string
}

export type ExtendedTravelOverviewDto = TravelOverviewDto & {
    // Is calculated during runtime and not actually returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
}

export type TravelFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        users?: string[]
        isBilled?: boolean
        files?: string[]
        fileOwners?: string[]
        invoiceApprovals?: string[]
    }

export type TravelSorting = {
    id: string
    date: string
    description: string
    'file.name': string
    'file.reference': string
}

export type TravelSortingType = BaseOverviewSortingType<TravelSorting>

type BaseTaskOverviewFilterProps = BaseTravelOverviewProps & BaseOverviewFilterProps<ExtendedTravelOverviewDto, TravelFilterType, TravelSortingType>

function TravelFilter(props: BaseTaskOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<TravelFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('registration.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.users || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('users', selectedOptions)}
                />
            </Field>
            {modules.has(ModuleType.Invoices) && (
                <Field label={translate('registration.filter.isBilled')} orientation={orientation}>
                    <BooleanCombobox
                        selectedOptions={[values.isBilled?.toString() || 'mixed']}
                        onOptionSelect={(_, data: CheckboxOnChangeData) =>
                            updateFieldValue('isBilled', data.checked === 'mixed' ? undefined : data.checked)
                        }
                    />
                </Field>
            )}
            {!props.hideFileField && (
                <Field label={translate('registration.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('registration.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}
const filterTagItems: TravelFilterType[] = [
    {
        from: DateTime.local().toISODate(),
        to: DateTime.local().toISODate(),
        tag: 'day',
        icon: <CalendarDayRegular />,
        title: 'travel.filter.today',
    },
    {
        from: DateTime.local().startOf('week').toISODate(),
        to: DateTime.local().endOf('week').toISODate(),
        tag: 'week',
        icon: <CalendarWorkWeekRegular />,
        title: 'travel.filter.week',
    },
    {
        from: DateTime.local().startOf('month').toISODate(),
        to: DateTime.local().endOf('month').toISODate(),
        tag: 'month',
        icon: <CalendarMonthRegular />,
        title: 'travel.filter.month',
    },
]

const headerSortingItems: TravelSortingType[] = [
    { property: ['date', 'id'], direction: SortDirection.Ascending },
    { property: ['date', 'id'], direction: SortDirection.Descending, default: true },
    { property: ['description', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.descriptionaz' },
    { property: ['description', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.descriptionza' },
    { property: ['file.name', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.fileaz' },
    { property: ['file.name', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.fileza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: TravelOverviewProps) {
    const { values, setValues } = useFormikContext<TravelFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    return (
        <div className={overviewStyles.flexGap}>
            {filterTagItems.map((tag, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === tag.tag}
                    onClick={() =>
                        setValues({
                            ...props.defaultFilterValues,
                            ...tag,
                        })
                    }
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <InteractionTagPrimary icon={tag.icon}> {translate(tag.title)}</InteractionTagPrimary>
                </InteractionTag>
            ))}
        </div>
    )
}

export default function BaseTravelOverview(props: BaseTravelOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    const defaultFilterValues = useMemo<TravelFilterType>(
        () => ({
            from: '',
            to: '',
            users: [],
            attributes: [],
            query: '',
            tag: 'day',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = (column?: number, filterValues?: TravelFilterType): Promise<TravelOverviewCount> => {
        return axios.get('/travel/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        column?: number,
        filterValues?: TravelFilterType,
        sortingValues?: TravelSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedTravelOverviewDto[]> => {
        return axios
            .get('/travel', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<OverviewItem[]>): ExtendedTravelOverviewDto[] =>
                (response?.data || []).map((item: OverviewItem) => ({
                    ...item,
                    turnover: getTravelTurnover(item.rate, item),
                    invoiceTurnover: item.price && {
                        amount: item.price.subtotal,
                        currency: item.price.currency,
                    },
                })),
            )
    }

    return (
        <BaseOverview<ExtendedTravelOverviewDto, TravelFilterType, TravelSortingType, TravelOverviewCount>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={TravelHeaderItem}
            listItemContent={(props: BaseTravelLineOverviewProps) => <TravelListItem {...props} />}
            listItemClassnames={() => [
                props.onItemSelected ? itemStyles.listTravelItemSelectable : itemStyles.listTravelItem,
                listItemSmall && (props.onItemSelected ? itemStyles.listTravelItemSmallSelectable : itemStyles.listTravelItemSmall),
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            filterContent={TravelFilter}
            filterFromJson={values => ({
                ...values,
                from: values.tag ? generateFromDate(values.tag) : values.from,
                to: values.tag ? generateToDate(values.tag) : values.to,
            })}
            filterToJson={values => ({
                ...values,
                from: values.tag ? '' : values.from,
                to: values.tag ? '' : values.to,
                icon: undefined,
                title: undefined,
            })}
            inputPlaceholderKey={'travel.filter.search'}
            {...props}
        />
    )
}
